import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Calendar } from "./ui/calendar";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SLOTS = ["9:00 AM", "10:30 AM", "12:00 PM", "2:00 PM", "4:00 PM", "6:00 PM"];

export default function Booking({ preselectServiceId, onSelected }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState(SLOTS[0]);
  const [duration, setDuration] = useState("30m");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      if (!serviceId) setServiceId(r.data[0]?.id || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (preselectServiceId) setServiceId(preselectServiceId);
  }, [preselectServiceId]);

  const submit = async () => {
    if (!user || typeof user !== "object") {
      toast.error("Sign in to book a 1-on-1");
      nav("/login");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", {
        service_id: serviceId,
        date: date.toISOString().slice(0, 10),
        time_slot: slot,
        duration,
        notes,
      });
      toast.success("Booking confirmed — Dev's team will reach out shortly.");
      setNotes("");
      onSelected && onSelected();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">1-on-1 Consultation</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-2 leading-tight">
              Book <span className="gold-text">direct time</span> with Dev
            </h2>
            <p className="text-[var(--text-secondary)] mt-3 max-w-md">
              Pick your pillar, lock a slot, drop what you need to talk about. Dev handles the rest.
            </p>
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
                <CalendarDays size={16} className="text-[var(--gold)]" /> Any day, any pillar
              </div>
              <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
                <Clock size={16} className="text-[var(--gold)]" /> 30-min quick check or 60-min deep dive
              </div>
            </div>
          </div>

          <div
            data-testid="booking-system-container"
            className="lg:col-span-7 gr-card p-6 md:p-8"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest font-mono text-[var(--gold)]">
                  Service pillar
                </label>
                <select
                  data-testid="booking-service-dropdown"
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                  className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)] focus:border-[var(--border-gold)] outline-none rounded-lg px-3 py-2.5 text-sm"
                >
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>

                <label className="text-xs uppercase tracking-widest font-mono text-[var(--gold)] mt-6 block">
                  Time slot
                </label>
                <select
                  data-testid="booking-time-slot-select"
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                  className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)] focus:border-[var(--border-gold)] outline-none rounded-lg px-3 py-2.5 text-sm"
                >
                  {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                <label className="text-xs uppercase tracking-widest font-mono text-[var(--gold)] mt-6 block">
                  Duration
                </label>
                <div className="mt-2 flex gap-2">
                  {["30m", "60m"].map((d) => (
                    <button
                      key={d}
                      data-testid={`booking-duration-${d}`}
                      onClick={() => setDuration(d)}
                      className={`flex-1 py-2 rounded-full text-xs font-mono uppercase tracking-widest border ${
                        duration === d
                          ? "bg-[var(--gold)] text-[#0a0a0e] border-[var(--gold)]"
                          : "border-[var(--border-subtle)] text-[var(--text-secondary)]"
                      }`}
                    >
                      {d === "30m" ? "30m · Quick Check" : "60m · Deep Dive"}
                    </button>
                  ))}
                </div>

                <label className="text-xs uppercase tracking-widest font-mono text-[var(--gold)] mt-6 block">
                  Notes to Dev
                </label>
                <textarea
                  data-testid="booking-notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="What are we solving?"
                  className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)] focus:border-[var(--border-gold)] outline-none rounded-lg px-3 py-2.5 text-sm resize-none"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest font-mono text-[var(--gold)]">
                  Pick a date
                </label>
                <div
                  data-testid="booking-date-picker"
                  className="mt-2 rounded-xl border border-[var(--border-subtle)] p-2 bg-[var(--bg-elevated)]"
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    disabled={{ before: new Date() }}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <button
              data-testid="booking-submit-button"
              onClick={submit}
              disabled={loading}
              className="gr-btn-gold w-full mt-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : null}
              Confirm Booking
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
